import { Legend, LineSeries } from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';

import { ProminenceRecalculationJobStatus } from '../../../api/monitoring/monitoring-account-configuration.api';
import { ChartMetricsEnum } from '../../../Common/Reports/enums/chart-metrics.enum';
import { ReportSeriesModel } from '../../Reports/models/report-series.model';
import { DateAndValueXYChartUnitEnum } from '../enums/date-and-value-x-y-chart-unit.enum';
import { manualChartExport } from '../helpers/manualChartExport.helper';
import { YAxisModel } from './axis.model';
import { CustomLegendItem } from './custom-legend-item.model';
import { DataPointModel } from './data-point.model';
import { DateAndValueXYChartOptionsModel } from './date-and-value-x-y-chart-options.model';
import { DateAndValueXYChart } from './date-and-value-x-y.chart';
import { ChartSizeHelper } from '../helpers/chart-size.helper';
import { isNumber } from 'underscore';

export class MultiSeriesDateAndValueXYChart extends DateAndValueXYChart {
    readonly singleSeries: boolean = false;
    readonly oneDay = 86400000;
    readonly oneHour = 3600000;

    declare amseries: LineSeries;
    declare options: DateAndValueXYChartOptionsModel;


    /**
     * Build XY Chart with x-axis of dates and y-axis with some numeric values
     */
    public initChart(element: HTMLElement): void {
        super.initChart(element, this.chartType, false);
        if (this.options.legendContainerElement && this.options.unit !== DateAndValueXYChartUnitEnum.PROMINENCE_LEVELS) {
            this.initCustomLegend();
        } else {
            // Set up the legend
            this.amchart.legend = new Legend();
            this.amchart.legend.scrollable = true;
            this.amchart.legend.contentAlign = 'left';
            this.amchart.legend.valign = 'bottom';
            this.amchart.legend.fontSize = 12;
            this.amchart.legend.labels.template.text = '{name}';
            this.amchart.legend.labels.template.truncate = true;

            this.amchart.legend.itemContainers.template.maxWidth = this.options?.maxItemWidth || 190;

            this.amchart.legend.itemContainers.template.paddingLeft = 10;
            this.amchart.legend.itemContainers.template.paddingTop = 2;
            this.amchart.legend.itemContainers.template.paddingBottom = 2;
        }

        this.amchart.scrollbarX.disabled = true;
        this.amchart.scrollbarX.focusable = false;
        this.amchart.scrollbarX.startGrip.tabindex = -1;
        this.amchart.scrollbarX.endGrip.tabindex = -1;
        this.amchart.scrollbarX.thumb.tabindex = -1;

        // Set up default yAxis on the left
        const defaultValueAxisConfig: YAxisModel = {
            key: 'series index 0',
            label: this.options.yAxisTitleText ? this.options.yAxisTitleText : '',
            valueLabel: this.options.yValueLabel,
            unit: this.options.unit,
            percentLabel: this.options.percentLabel,
            color: this.options.isMultiAxis ? this.styles.colorMap['seriesColor0'] : null
        };

        // it should be empty array by default, but drilldown "technically" has 1 chart per tab.. so it keeps appending when not needed
        this.valueAxesConfig = [];
        this.valueAxes = [];

        this.defaultValueAxis = this.addYAxis(defaultValueAxisConfig);
        this.valueAxesConfig.push(defaultValueAxisConfig);
        this.valueAxes.push(this.defaultValueAxis);
        for (let i = 0; i < this.series.length; i++) {
            if (this.hasValidMentionsSource(this.series[i])) {
                if (this.options.isMultiAxis && i !== 0) {
                    // Set up multi yAxis on the right side of the chart
                    const yAxis: YAxisModel = {
                        key: `series index ${i}`,
                        label: '',
                        valueLabel: this.options.yValueLabel,
                        unit: this.options.unit,
                        percentLabel: this.options.percentLabel,
                        color: this.series[i].color,
                        position: 'right'
                    };
                    this.valueAxesConfig.push(yAxis);
                    this.valueAxes.push(this.addYAxis(yAxis, this.defaultValueAxis));
                    this.addSeries(i, this.series[i], yAxis.key);
                } else {
                    this.addSeries(i, this.series[i], defaultValueAxisConfig.key);
                }
            }
        }
        if (this.options.yAxes?.length === 1) {
            this.valueAxesConfig.push(...this.options.yAxes);
            this.options.yAxes.forEach(yAxis => {
                this.valueAxes.push(
                    this.addYAxis(yAxis, this.defaultValueAxis)
                );
            });
            this.amchart.series.values.forEach((series, index) => {
                if (index === 0) {
                    return;
                }
                series.yAxis = this.getValueAxisByKey(this.options.yAxes[0].key);
            });
        }

        if (this.options.responsive) {
            if (this.amchart.legend) {
                this.amchart.legend.dy = 20;
            }
            this.amchart.events.on('sizechanged', ev => {
                const chart = ev.target;

                if (chart.pixelWidth <= 768) {
                    chart.series.values.map(series => {
                        series.tooltip.fontSize = 14;
                    });
                    if (chart.legend) {
                        chart.legend.fontSize = 14;
                    }
                } else {
                    chart.series.values.map(series => {
                        series.tooltip.fontSize = 16;
                    });
                    if (chart.legend) {
                        chart.legend.fontSize = 16;
                    }
                }
            });
        } else {
            const smallChartWidth = ChartSizeHelper.getWidthAtMinCanvas(3, 1);
            const smallChartHeight = ChartSizeHelper.getCellHeight();
            const xsChartHeight = ChartSizeHelper.getHeight(1, 1); // for dashboard recent topic
            this.amchart.events.on('sizechanged', ev => {
                const chart = ev.target;
                if (chart.pixelHeight <= smallChartHeight || chart.pixelWidth <=smallChartWidth) {
                    if (this.titleLabel) {
                        this.titleLabel.marginBottom = 0;
                        this.header.marginBottom = 5;
                    }
                    this.dateAxis.fontSize = 12;
                    if (chart.pixelHeight <= xsChartHeight) {
                        this.amchart.paddingBottom = 10;
                        this.valueAxes.forEach(axis => {
                            axis.title.dy = -20;
                            axis.fontSize = 12;
                        });
                        if (this.amchart.legend) {
                            this.amchart.legend.maxHeight = 20;
                            this.amchart.legend.itemContainers.template.scale = 0.75;
                            this.amchart.legend.itemContainers.template.paddingTop = 0;
                            this.amchart.legend.itemContainers.template.paddingBottom = 0;
                        }
                    } else {
                        this.amchart.paddingBottom = 15;
                        this.valueAxes.forEach(axis => {
                            axis.fontSize = 12;
                        });
                        if (this.amchart.legend) {
                            this.amchart.legend.maxHeight = 54;
                            this.amchart.legend.itemContainers.template.scale = 0.9;
                            this.amchart.legend.itemContainers.template.paddingTop = 2;
                            this.amchart.legend.itemContainers.template.paddingBottom = 2;
                        }
                    }
                } else {
                    this.amchart.paddingBottom = 15;
                    if (this.titleLabel) {
                        this.titleLabel.marginBottom = 5;
                    }
                    this.dateAxis.fontSize = 13;
                    this.valueAxes.forEach(axis => {
                        axis.fontSize = 14;
                    });

                    if (this.amchart.legend) {
                        this.amchart.legend.maxHeight = 500;
                        this.amchart.legend.itemContainers.template.scale = 1;
                        this.amchart.legend.itemContainers.template.paddingTop = 3;
                        this.amchart.legend.itemContainers.template.paddingBottom = 3;
                    }
                }

                this.amchart.legend.afterDraw();
            });
        }

        // @HACK for some reason, if there's no data, the legend is too close to x Axis
        if (this.options.unit === DateAndValueXYChartUnitEnum.PROMINENCE_LEVELS && this.options.prominenceStatus !== ProminenceRecalculationJobStatus.DONE) {
            this.amchart.legend.dy = 10;
        }

        const reportModel = this.series[0] as ReportSeriesModel;
        if (reportModel?.metric === ChartMetricsEnum.ave && reportModel?.styles?.currency){
            this.valueAxes[0].numberFormatter = new am4core.NumberFormatter();
            if (reportModel.styles?.currencySymbol){
                this.valueAxes[0].numberFormatter.numberFormat = `${reportModel.styles.currencySymbol}#,###.#a`;
            }else if (reportModel.styles?.currency){
                this.valueAxes[0].numberFormatter.numberFormat = `#,###.#a ${reportModel.styles.currency}`;
            }
        }

        if (this.options.centerLegend) {
            this.amchart.legend.contentAlign = 'center';
            this.amchart.legend.fontSize = 14;
        }

        if (this.options.verticalLines?.length > 0) {
            this.options.verticalLines.forEach((lineDef) => {
                this.addAxisLine(this.dateAxis, lineDef, true);
            });
        }

        if (this.options.doReadyInInit) {
            this.ready.next(true);
        }

        if (isNumber(this.amchart.paddingRight)) {
            (this.amchart.paddingRight += 15);
        }
    }

    getCustomLegendItems(): CustomLegendItem[] {
        const items: CustomLegendItem[] = [];
        this.amchart.series.each((series) => {
            if (!series) {
                return;
            }
            const item: CustomLegendItem = {
                label: series.name,
                color: series.stroke.toString(),
                // TODO: this works, but we want to disable the item in the legend, which isn't available yet
                // click: () => {
                //     series.visible = !series.visible;
                // }
            };
            items.push(item);
        });

        return items;
    }

    /**
     * Updates the current presentation of data in the chart
     */
    public updateData(data: DataPointModel[][]): void {
        // if it is prominence chart, and calculation is not completed, do not update data
        if (!this.options.responsive
            && this.options.unit === DateAndValueXYChartUnitEnum.PROMINENCE_LEVELS
            && this.options.prominenceStatus !== ProminenceRecalculationJobStatus.DONE) {
            return;
        }

        this.rawData = data;

        if (!data?.includes(null) && this.isContentEmpty()) {
            this.valueAxes.forEach(axis => axis.disabled = true);
            this.dateAxis.disabled = true;
            this.amchart.scrollbarX.disabled = true;
            this.showNoContentMessage();
            this.noContentLabel.dom.style.display = 'block';
            this.amchart?.legend?.hide(0);
        } else {
            this.amchart?.legend?.show(0);
            this.valueAxes.forEach(axis => axis.disabled = false);
            this.dateAxis.disabled = false;
            if (this.noContentLabel) {
                this.noContentLabel.dom.style.display = 'none';
                this.noContentLabel.text = '';
            }
        }

        if (!data) {
            this.amchart.data = null;

            return;
        }

        const flatData: DataPointModel[] = [];
        // calculate the min/max and step size
        let minXValue: number = Number.MAX_VALUE;
        let maxXValue: number = Number.MIN_VALUE;
        let stepSize: number = null;
        const yValueKeys = [];

        for (const [dataPointsIndex, dataPoints] of data.entries()) {
            if (dataPoints && dataPoints.length) {
                if (dataPoints[0].xValue < minXValue) {
                    minXValue = dataPoints[0].xValue;
                }
                if (dataPoints[dataPoints.length - 1].xValue > maxXValue) {
                    maxXValue = dataPoints[dataPoints.length - 1].xValue;
                }

                if (!stepSize && dataPoints.length >= 2) {
                    stepSize = dataPoints[1].xValue - dataPoints[0].xValue;
                }

                yValueKeys.push('yValue' + dataPointsIndex);
            }
        }

        if (!yValueKeys.length) {
            this.amchart.data = null;

            return;
        }

        // If stepSize is still null somehow, it's really bad, so default it to this.oneDay
        if (!stepSize) {
            stepSize = this.oneDay;
        }

        // Add all the xValues to the flatData array
        for (let i = minXValue; i <= maxXValue; i += stepSize) {
            flatData.push({
                xValue: i,
                // display tooltip as hour:second or date based on stepSize
                label: stepSize >= this.oneDay ? new Date(i).toDateString() : new Date(i).toTimeString().substring(0, 5)
            });
        }

        // Add data to flatData from each dataPoints series
        for (const [dataPointsIndex, dataPoints] of data.entries()) {
            if (!dataPoints || !dataPoints.length) {
                continue;
            }

            if ((dataPoints[0].xValue === undefined) || (dataPoints[0].xValue === null)) {
                continue;
            }
            // Find the starting point of the master array for this dataPoints[]
            let i: number = Math.floor((dataPoints[0].xValue - minXValue) / stepSize);

            for (const dataPoint of dataPoints) {
                if (i === flatData.length) {
                    const nextPointValue = minXValue + i * stepSize;
                    flatData[i] = {
                        xValue: nextPointValue,
                        // display tooltip as hour:second or date based on stepSize
                        label: stepSize >= this.oneDay ? new Date(nextPointValue).toDateString() : new Date(nextPointValue).toTimeString().substring(0, 5)
                    };
                }
                flatData[i]['yValue' + dataPointsIndex] = dataPoint.yValue;
                flatData[i]['startTime'] = dataPoint.startTime;
                flatData[i]['endTime'] = dataPoint.endTime;
                flatData[i]['xValueStartTime'] = dataPoint.xValueStartTime;
                flatData[i]['xValueEndTime'] = dataPoint.xValueEndTime;
                flatData[i]['rawDateFacetTo'] = dataPoint.rawDateFacetTo;
                flatData[i]['rawDateFacetFrom'] = dataPoint.rawDateFacetFrom;
                flatData[i]['mentionCount'] = dataPoint.mentionCount;
                flatData[i]['viewMentionUrl' + dataPointsIndex] = dataPoint.viewMentionUrl;

                flatData[i][`series${dataPointsIndex}Url`] = dataPoint.viewUrl;

                flatData[i]['prEventName'] = dataPoint.prEventName;
                flatData[i]['prEventType'] = dataPoint.prEventType;
                flatData[i]['prEventDate'] = dataPoint.prEventDate;

                i++;
            }
        }

        // make sure that all the series have a value at every point (0 if not set)
        for (const dataPoint of flatData) {
            for (const yValueKey of yValueKeys) {
                if (dataPoint[yValueKey] === undefined) {
                    dataPoint[yValueKey] = 0;
                }
            }
        }

        this.amchart.data = JSON.parse(JSON.stringify(flatData));

        // override the legend text
        for (let i = 0; i < this.amchart.series.length; i++) {
            if (this.series[i].label) {
                this.amchart.series.values[i].name = this.series[i].label;
            }
        }

        this.updateSeriesStyles();
        if (!this.options.doReadyInInit) {
            this.ready.next(true);
        }
    }

    // just for the recent topics chart (dashboard).. checks to see if it's a valid series before adding
    // so it will not show up in the legend if number of topics is less than 4 (or # they select)
    public hasValidMentionsSource(series): boolean {
        // only recent topics (dashboard) has dataIndex
        if (series.hasOwnProperty('dataIndex')) {
            if (series.label) {
                if (!series?.dataProvider?.hasError && series?.dataProvider?.value) {
                    return true;
                }
                return !!(series?.mentions?.topicId);
            } else {
                return false;
            }
        }
        return true;
    }

    public setExportStyles(scale?: number, keepFontsize?: boolean): void {
        super.setExportStyles(scale, keepFontsize);
        this.amchart.legend.itemContainers.template.width = 180;
    }

    public unsetExportStyles(): void {
        this.amchart.legend.itemContainers.template.width = 100;
        super.unsetExportStyles();
    }

    /**
     * @TODO
     */
    public updateSeriesStyles(): void {}

    /**
     * Exports data using amcharts
     */
    public export(type: keyof am4core.IExportOptions, filename?: string): Promise<boolean> {
        this.amchart.exporting.dateFormatter.inputDateFormat = 'yyyy/MM/dd hh:mm';
        this.amchart.exporting.dateFields = new am4core.List<string>(['startTime', 'endTime']);
        this.amchart.exporting.dateFormat = 'yyyy/MM/dd hh:mm';

        const exportType = this.exportTypes.find(exportOption => exportOption === type);
        // have to have manually export to properly convert the dates to ignore the minutes
        if (exportType) {
            this.setDataFields(type, filename);

            return;
        }
        this.amchart.exporting.filePrefix = filename;
        this.amchart.exporting.useWebFonts = false;

        return this.amchart.exporting.export(type);
    }

    /**
     * Sets the properties and labels that will be exported
     */
    public setDataFields(type: keyof am4core.IExportOptions, filename?: string): void {
        const dataFields: object = {};
        if (this.options.unit === DateAndValueXYChartUnitEnum.PROMINENCE_LEVELS){
            dataFields['mentionCount'] = this.options.mentionsLabel;
            // for prominence chart, yValue0 is average prominence value, and the label is 'average prominence'
            dataFields['yValue0'] = this.options.averageProminenceLabel;
        }

        dataFields['label'] = this.options.label;
        dataFields['xValueStartTime'] = this.options.startTimeLabel;
        dataFields['xValueEndTime'] = this.options.endTimeLabel;

        if (this.options.unit !== DateAndValueXYChartUnitEnum.PROMINENCE_LEVELS) {
            // if it is not prominence chart, the label for yValue should be its series label name + metric label
            this.series.forEach((series, index) => {
                dataFields[`yValue${index}`] = series.label + ' - ' + this.options.yValueLabel;
            });
        }

        if (this.options.events?.length > 0) {
            dataFields['prEventName'] =  this.options.eventNameLabel;
            dataFields['prEventType'] =  this.options.eventTypeLabel;
            dataFields['prEventDate'] =  this.options.eventDateLabel;
        }

        const useGroupDataPoints = this.options.groupIntervals?.length === 1 && (
            this.options.groupIntervals[0].timeUnit === 'week'
                || this.options.groupIntervals[0].timeUnit === 'month'
                || this.options.groupIntervals[0].timeUnit === 'year'
        );

        if (useGroupDataPoints && this.groupDataPoints?.length) {
            manualChartExport.exportFlatSeriesData(type, this.groupDataPoints, dataFields, filename);
        } else {
            manualChartExport.exportFlatSeriesData(type, this.amchart.data, dataFields, filename);
        }
    }

    public isContentEmpty(): boolean {
        if (!this.rawData) {
            return;
        }
        for (const value of this.rawData) {
            if (value && value.length > 0) {
                return false;
            }
        }
        return true;
    }

    public clone(): MultiSeriesDateAndValueXYChart {
        return new MultiSeriesDateAndValueXYChart(
            this.series.map(s => s.clone ? s.clone() : s),
            this.options,
            this.metadata
        );
    }
}
